import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  offlineApi,
  type InvenSyncList,
  type InvenSyncMeta,
  type InvenSyncRow,
  type InvenSyncStatus,
  type PosStoreMeta,
} from '../../api/offline.js';
import { Spinner, ErrorBox, EmptyBox, Field, Pagination, StatusBadge } from '../../components/ui.js';
import { ConfirmModal } from '../../components/Modal.js';
import { num } from '../../lib/format.js';

/**
 * 포스시스템 재고연동.
 *
 * 매장 POS(SQL Server) `dbo.Goods` 를 보여주고, **BarCode1 을 한 줄씩 손으로
 * 고친다**. 줄마다 입력칸이 있고, 그 줄에 맞는 xadmin 바코드를 찾았으면 '제안'
 * 으로 옆에 띄워 한 번 눌러 채울 수 있다 - 저절로 저장되지는 않는다.
 *
 * 묶음 연동 버튼('의정부/청담 바코드 연동')과 cate_cd 자동 채우기는 2026-10-08
 * 요청으로 걷어냈다. `cate_cd` 는 POS 의 현재 값만 보여준다.
 *
 * 같은 날 바로, **gcode 로 찾은 줄만** 모아 일괄 저장하는 버튼을 다시 넣었다
 * (gcodeRemapped - "POS 코드가 옛 상품코드였고 shop_goods.gcode 로 지금
 * 상품을 유일하게 찾은" 줄만). 전체를 쓸어 넣던 예전 버튼과 달리 새 서버
 * 엔드포인트를 만들지 않았다 - 한 줄씩 쓰는 기존 PATCH(setBarcode1)를 그
 * 상태의 줄 수만큼 반복해서 부른다. 쓰기 경로를 하나로 묶어 둬야 "한 줄
 * 수정은 막았는데 일괄은 안 막았다" 같은 어긋남이 안 생긴다.
 *
 * 운영시간(KST) 밖이면 **조회 자체를 하지 않는다** - 서버가 내려준 meta 의
 * open 을 보고 안내만 띄운다. 그래서 닫혀 있는 매장의 MSSQL 에는 접속을
 * 시도하지도 않는다 (서버에도 같은 문턱이 한 겹 더 있다).
 */

/** 상태 칸의 색. 고칠 거리가 남은 것과 사람이 봐야 하는 것을 가른다. */
const STATUS_TONE: Record<InvenSyncStatus, 'ok' | 'info' | 'warn' | 'danger' | 'neutral'> = {
  linked: 'ok',
  ready: 'info',
  gcodeRemapped: 'info',
  gcodeAmbiguous: 'warn',
  gcodeOptionLost: 'warn',
  noProduct: 'danger',
  noOption: 'danger',
  noBarcode: 'warn',
  multiBarcode: 'warn',
  badFormat: 'danger',
  oddBarcode: 'warn',
  posIsXadminBarcode: 'warn',
  duplicatePos: 'warn',
};

/** 요약 줄·필터 드롭다운에 보여줄 순서. */
const SUMMARY_ORDER: InvenSyncStatus[] = [
  'ready',
  'gcodeRemapped',
  'linked',
  'gcodeAmbiguous',
  'gcodeOptionLost',
  'noProduct',
  'noOption',
  'noBarcode',
  'multiBarcode',
  'posIsXadminBarcode',
  'oddBarcode',
  'duplicatePos',
  'badFormat',
];

/** dbo.Goods.BarCode1 은 nvarchar(40) 이다. 서버도 같은 값으로 막는다. */
const BARCODE1_MAX_LENGTH = 40;

/** gcode 일괄 적용의 진행 상태. */
type BulkApplyState =
  | { kind: 'idle' }
  | { kind: 'confirm' }
  | { kind: 'running'; applied: number; skipped: number }
  | { kind: 'done'; applied: number; skipped: number };

function StoreTabs({
  stores,
  current,
  onPick,
}: {
  stores: PosStoreMeta[];
  current: string;
  onPick: (key: string) => void;
}) {
  return (
    <div className="tab-group" aria-label="매장">
      {stores.map((store) => (
        <button
          key={store.key}
          type="button"
          className={store.key === current ? 'tab-btn tab-btn--active' : 'tab-btn'}
          aria-pressed={store.key === current}
          onClick={() => onPick(store.key)}
        >
          {store.label}
        </button>
      ))}
    </div>
  );
}

type SaveState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  /** 그 사이 다른 사람이 고쳤다. 화면의 값을 서버 값으로 되돌려 둔다. */
  | { kind: 'conflict' }
  | { kind: 'error'; message: string };

/**
 * BarCode1 입력칸 한 개.
 *
 * 저장은 **Enter 또는 칸을 벗어날 때** 한 번만 보낸다 - 글자마다 POS 에 쓰면
 * 매장 서버를 두드리는 일이 된다. 값이 안 바뀌었으면 아무것도 보내지 않는다.
 */
function Barcode1Cell({
  row,
  disabled,
  onSave,
}: {
  row: InvenSyncRow;
  disabled: boolean;
  onSave: (next: string) => Promise<{ outcome: 'updated' | 'changedElsewhere'; barcode1: string | null }>;
}) {
  const stored = row.barcode1 ?? '';
  const [draft, setDraft] = useState(stored);
  const [state, setState] = useState<SaveState>({ kind: 'idle' });
  /**
   * 방금 저장해서 내가 바꾼 값. 저장이 성공하면 목록 사본도 같이 바뀌는데,
   * 그 변화까지 "밖에서 값이 바뀌었다" 로 보면 아래 effect 가 '저장됨' 표시를
   * 바로 지워 버린다 - 눌러도 아무 일도 안 일어난 것처럼 보인다.
   */
  const justSaved = useRef<string | null>(null);

  // 목록을 다시 읽어 값이 바뀌면 입력칸도 따라간다 (다른 탭·다른 페이지에서
  // 돌아왔을 때 옛 초안이 남아 있지 않게). 내가 바꾼 값은 건너뛴다.
  useEffect(() => {
    if (justSaved.current === stored) {
      justSaved.current = null;
      return;
    }
    setDraft(stored);
    setState({ kind: 'idle' });
  }, [stored]);

  async function commit() {
    const next = draft.trim();
    // 값이 그대로면 매장 POS 를 두드릴 이유가 없다. 저장 중에 또 들어오는 것도
    // 막는다 - Enter 로 저장한 뒤 칸을 벗어나면 blur 가 한 번 더 부른다.
    if (next === stored.trim() || state.kind === 'saving') {
      if (state.kind !== 'saving') setState({ kind: 'idle' });
      return;
    }
    setState({ kind: 'saving' });
    try {
      const result = await onSave(next);
      // 목록 사본이 이 값으로 바뀔 것이다 - 그 변화에 표시가 지워지지 않게 적어 둔다.
      justSaved.current = result.barcode1 ?? '';
      setDraft(result.barcode1 ?? '');
      setState({ kind: result.outcome === 'updated' ? 'saved' : 'conflict' });
    } catch (err) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }

  return (
    <div className="inven-sync-edit">
      <input
        className="input inven-sync-edit__input"
        type="text"
        inputMode="numeric"
        maxLength={BARCODE1_MAX_LENGTH}
        value={draft}
        disabled={disabled || state.kind === 'saving'}
        placeholder="(비어 있음)"
        aria-label={`${row.posBarcode} 의 BarCode1`}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            // blur() 를 거치지 않고 바로 저장한다 - 포커스가 없는 칸에서는
            // blur 이벤트가 안 나서(브라우저·jsdom 모두) Enter 가 먹히지 않는다.
            event.preventDefault();
            void commit();
          }
          if (event.key === 'Escape') {
            setDraft(stored);
            setState({ kind: 'idle' });
          }
        }}
      />
      {/* 찾은 바코드가 있고 아직 그 값이 아니면 한 번 눌러 채운다 */}
      {row.xadminBarcode && draft.trim() !== row.xadminBarcode && (
        <button
          type="button"
          className="btn btn--ghost btn--sm inven-sync-edit__suggest"
          disabled={disabled || state.kind === 'saving'}
          title={`찾은 바코드 ${row.xadminBarcode} 를 칸에 넣습니다 (저장은 따로)`}
          onClick={() => setDraft(row.xadminBarcode!)}
        >
          제안 {row.xadminBarcode}
        </button>
      )}
      {state.kind === 'saving' && <span className="inven-sync-edit__state">저장 중…</span>}
      {state.kind === 'saved' && (
        <span className="inven-sync-edit__state inven-sync-edit__state--ok">저장됨</span>
      )}
      {state.kind === 'conflict' && (
        <span className="inven-sync-edit__state inven-sync-edit__state--warn" role="alert">
          그 사이 다른 사람이 고쳤습니다. 지금 값으로 되돌렸습니다.
        </span>
      )}
      {state.kind === 'error' && (
        <span className="inven-sync-edit__state inven-sync-edit__state--error" role="alert">
          {state.message}
        </span>
      )}
    </div>
  );
}

function Row({
  row,
  labels,
  disabled,
  onSave,
}: {
  row: InvenSyncRow;
  labels: Record<InvenSyncStatus, string>;
  disabled: boolean;
  onSave: (row: InvenSyncRow, next: string) => Promise<{ outcome: 'updated' | 'changedElsewhere'; barcode1: string | null }>;
}) {
  return (
    <tr>
      <td className="inven-sync-code">{row.posBarcode}</td>
      <td className="inven-sync-edit-cell">
        <Barcode1Cell row={row} disabled={disabled} onSave={(next) => onSave(row, next)} />
      </td>
      <td className={row.cateCd === null ? 'td-muted' : 'inven-sync-code'}>
        {row.cateCd === null ? 'NULL' : row.cateCd}
      </td>
      <td style={{ textAlign: 'right' }}>
        {row.goodsCode ?? '-'}
        {/* 옛 코드로 들어와 있던 줄은 무엇이 무엇으로 바뀌었는지 보여준다 */}
        {row.oldGoodsCode !== null ? <span className="td-sub">옛 {row.oldGoodsCode}</span> : null}
      </td>
      <td style={{ textAlign: 'right' }}>{row.opCode ?? '-'}</td>
      <td>
        {row.gname || '-'}
        {row.opname ? <span className="td-sub">{row.opname}</span> : null}
      </td>
      <td>
        <StatusBadge label={labels[row.status]} tone={STATUS_TONE[row.status]} />
        {row.barcodeCount > 1 ? <span className="td-sub">바코드 후보 {row.barcodeCount}개</span> : null}
      </td>
    </tr>
  );
}

export function InvenSyncPage() {
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();

  const meta = useQuery<InvenSyncMeta>({
    queryKey: ['offline', 'inven-sync', 'meta'],
    queryFn: () => offlineApi.invenSyncMeta(),
    // 운영시간 경계를 넘어가면 화면이 알아서 바뀌어야 한다 - 1분마다 다시 본다.
    refetchInterval: 60_000,
  });

  const stores = meta.data?.stores ?? [];
  const storeKey = params.get('store') ?? meta.data?.defaultStore ?? '';
  const store = stores.find((s) => s.key === storeKey) ?? stores[0];
  const status = (params.get('status') ?? '') as InvenSyncStatus | '';
  const q = params.get('q') ?? '';
  const page = Number(params.get('page') ?? 1);

  function update(next: Record<string, string | number | undefined>) {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value === undefined || value === '') merged.delete(key);
      else merged.set(key, String(value));
    }
    setParams(merged);
  }

  // 운영시간 밖이거나 접속 설정이 비어 있으면 조회를 시작하지 않는다.
  const canQuery = Boolean(store?.open && !store.configProblem);
  const listKey = ['offline', 'inven-sync', store?.key, status, q, page];

  const list = useQuery({
    queryKey: listKey,
    queryFn: () => offlineApi.invenSync({ store: store!.key, status, q, page }),
    enabled: canQuery && Boolean(store),
  });

  /**
   * '다시 읽기'. 서버는 매장별로 읽은 결과를 3분 들고 있어서(페이지를 넘길
   * 때마다 POS 전체를 다시 읽지 않도록) 평범한 refetch 로는 그 사본이 그대로
   * 온다. 이 버튼만 fresh 를 달아 보내고, 받은 값을 지금 보고 있는 조회의
   * 자리에 그대로 넣는다.
   */
  const reload = useMutation({
    mutationFn: () => offlineApi.invenSync({ store: store!.key, status, q, page, fresh: true }),
    onSuccess: (data) => {
      qc.setQueryData(listKey, data);
      // 다른 페이지·필터의 사본은 버린다 - 같은 시점을 보고 있어야 한다.
      void qc.invalidateQueries({ queryKey: ['offline', 'inven-sync'], refetchType: 'none' });
    },
  });

  /**
   * 한 줄 저장.
   *
   * 성공하면 **목록을 다시 읽지 않고** 그 줄만 자리에서 고친다 - 다시 읽으면
   * POS 전체 읽기 + xadmin 지도 만들기(약 1초)가 한 칸 고칠 때마다 돈다.
   * 서버도 자기 사본의 그 줄만 고쳐 두므로 다음 페이지 이동에서 어긋나지 않는다.
   */
  async function saveRow(row: InvenSyncRow, next: string) {
    const result = await offlineApi.invenSyncSetBarcode1({
      store: store!.key,
      posBarcodeStored: row.posBarcodeStored,
      barcode1: next === '' ? null : next,
      expect: row.barcode1,
    });

    qc.setQueryData<InvenSyncList>(listKey, (prev) =>
      prev
        ? {
            ...prev,
            rows: prev.rows.map((r) =>
              r.posBarcodeStored === row.posBarcodeStored
                ? {
                    ...r,
                    barcode1: result.barcode1,
                    // 값이 들어갔으면 '이미 연동됨' 이고 제안은 사라진다 -
                    // 서버의 matchPosRows 가 붙이는 상태와 같아야 한다.
                    ...(result.barcode1 === null
                      ? {}
                      : { status: 'linked' as InvenSyncStatus, xadminBarcode: null }),
                  }
                : r,
            ),
          }
        : prev,
    );
    // 비웠으면 어느 상태로 돌아가는지는 전체 줄을 봐야 알 수 있다 - 서버가
    // 사본을 버렸으니 다음 조회가 새로 읽는다.
    if (result.barcode1 === null) {
      void qc.invalidateQueries({ queryKey: ['offline', 'inven-sync'], refetchType: 'none' });
    }
    return result;
  }

  const labels = meta.data?.statusLabels;
  const rows = list.data?.rows ?? [];

  const gcodeTotal = list.data?.summary.byStatus.gcodeRemapped ?? 0;
  const [bulk, setBulk] = useState<BulkApplyState>({ kind: 'idle' });

  /**
   * gcodeRemapped 전부를 한 줄씩 저장한다.
   *
   * 새 쓰기 경로를 만들지 않고 기존 saveRow(= PATCH /inven-sync/barcode1)를
   * 반복한다. status=gcodeRemapped 로 **매번 1페이지를 다시 읽는다** - 저장에
   * 성공한 줄은 'linked' 로 바뀌어 이 필터에서 빠지므로, 1페이지가 비면 남은
   * 줄이 없다는 뜻이다(페이지를 전진하면 방금 빠진 줄들 때문에 뒤 페이지가
   * 밀려 일부를 건너뛸 수 있다 - 그래서 늘 1페이지로 돌아간다).
   *
   * 한 줄 실패(충돌·오류)는 건너뛰고 계속한다 - 하나가 막혔다고 나머지까지
   * 멈추면 사람이 다시 눌러야 하는 줄이 더 늘어난다. 상한(50회)은 끝없이
   * 도는 것을 막는 안전장치일 뿐, 실제로는 훨씬 적게 끝난다.
   */
  async function runGcodeBulkApply() {
    if (!store) return;
    setBulk({ kind: 'running', applied: 0, skipped: 0 });

    let applied = 0;
    let skipped = 0;
    for (let loop = 0; loop < 50; loop += 1) {
      const page = await offlineApi.invenSync({
        store: store.key,
        status: 'gcodeRemapped',
        page: 1,
        perPage: 200,
      });
      if (page.rows.length === 0) break;

      for (const row of page.rows) {
        if (!row.xadminBarcode) {
          skipped += 1;
          continue;
        }
        try {
          const result = await saveRow(row, row.xadminBarcode);
          if (result.outcome === 'updated') applied += 1;
          else skipped += 1;
        } catch {
          skipped += 1;
        }
        setBulk({ kind: 'running', applied, skipped });
      }
    }

    setBulk({ kind: 'done', applied, skipped });
    void qc.invalidateQueries({ queryKey: ['offline', 'inven-sync'] });
  }

  return (
    <section>
      <header className="page-head">
        <div>
          <h1 className="page-title">포스시스템 재고연동</h1>
          <p className="page-desc">
            매장 POS 의 <code>dbo.Goods</code> 를 보여주고, <code>BarCode1</code> 을 줄마다 직접
            고칩니다. 칸에 넣고 <kbd>Enter</kbd> 또는 칸을 벗어나면 그 줄만 저장됩니다. 그 줄에 맞는
            xadmin 바코드를 찾았으면 <strong>제안</strong> 으로 띄우니 눌러서 채운 뒤 저장하세요 —
            저절로 들어가지는 않습니다.
          </p>
        </div>
        <div className="page-head__actions">
          {(list.isFetching || reload.isPending) && <span className="page-head__busy">갱신 중…</span>}
          {store && canQuery && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={list.isFetching || reload.isPending}
              onClick={() => reload.mutate()}
            >
              다시 읽기
            </button>
          )}
        </div>
      </header>

      {meta.isPending ? (
        <Spinner />
      ) : meta.isError ? (
        <ErrorBox error={meta.error} onRetry={() => void meta.refetch()} />
      ) : !store || !labels ? (
        <EmptyBox>등록된 매장이 없습니다.</EmptyBox>
      ) : (
        <>
          <StoreTabs
            stores={stores}
            current={store.key}
            onPick={(key) => update({ store: key, page: 1, status: '', q: '' })}
          />

          {store.configProblem ? (
            <div className="notice notice--warn" role="alert">
              {store.configProblem}
            </div>
          ) : !store.open ? (
            <div className="notice notice--warn" role="status">
              {store.closedMessage}
              <span className="td-sub">
                서버 기준 지금 {meta.data.now} (KST). 운영시간 밖에는 POS 에 접속하지 않습니다.
              </span>
            </div>
          ) : (
            <>
              <div className="filters">
                <Field label="상태">
                  <select
                    className="input"
                    value={status}
                    onChange={(event) => update({ status: event.target.value, page: 1 })}
                  >
                    <option value="">전체</option>
                    {SUMMARY_ORDER.map((s) => (
                      <option key={s} value={s}>
                        {labels[s]}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="검색">
                  <input
                    className="input"
                    type="search"
                    defaultValue={q}
                    placeholder="POS 바코드 / BarCode1 / 상품코드 / 상품명"
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter') return;
                      event.preventDefault();
                      update({ q: event.currentTarget.value, page: 1 });
                    }}
                  />
                </Field>
                {(status || q) && (
                  <button
                    type="button"
                    className="btn btn--ghost filters__reset"
                    onClick={() => update({ status: '', q: '', page: 1 })}
                  >
                    필터 초기화
                  </button>
                )}
              </div>

              {reload.isError && <ErrorBox error={reload.error} onRetry={() => reload.mutate()} />}

              {list.isPending ? (
                <Spinner label="POS 를 읽고 xadmin 과 맞추는 중… (수 초 걸립니다)" />
              ) : list.isError ? (
                <ErrorBox error={list.error} onRetry={() => void list.refetch()} />
              ) : (
                <>
                  <div className="summary">
                    <div className="summary__item">
                      <span className="summary__label">POS 전체</span>
                      <strong className="summary__value">{num(list.data.summary.total)}건</strong>
                    </div>
                    {SUMMARY_ORDER.filter((s) => list.data.summary.byStatus[s] > 0).map((s) => (
                      <button
                        type="button"
                        key={s}
                        className={status === s ? 'summary__item summary__item--on' : 'summary__item'}
                        onClick={() => update({ status: status === s ? '' : s, page: 1 })}
                      >
                        <span className="summary__label">{labels[s]}</span>
                        <strong className="summary__value">
                          {num(list.data.summary.byStatus[s])}건
                        </strong>
                      </button>
                    ))}
                  </div>

                  {gcodeTotal > 0 && (
                    <div className="notice notice--row">
                      <span>
                        옛 상품코드로 찾은 <strong>{num(gcodeTotal)}건</strong>이 있습니다. 한 줄씩
                        제안을 눌러 넣는 대신 모아서 한 번에 저장할 수 있습니다.
                      </span>
                      <button
                        type="button"
                        className="btn btn--primary btn--sm"
                        disabled={bulk.kind === 'running'}
                        onClick={() => setBulk({ kind: 'confirm' })}
                      >
                        gcode로 찾은 {num(gcodeTotal)}건 일괄 저장
                      </button>
                    </div>
                  )}

                  {bulk.kind === 'running' && (
                    <div className="notice notice--warn" role="status">
                      저장 중… (적용 {num(bulk.applied)}건 · 건너뜀 {num(bulk.skipped)}건)
                    </div>
                  )}
                  {bulk.kind === 'done' && (
                    <div className="notice notice--ok notice--row" role="status">
                      <span>
                        일괄 저장 완료 - 적용 {num(bulk.applied)}건
                        {bulk.skipped > 0 && (
                          <>
                            , 건너뜀 {num(bulk.skipped)}건(이미 바뀌었거나 저장에 실패한 줄 - 목록에서
                            직접 확인하세요)
                          </>
                        )}
                        .
                      </span>
                      <button type="button" className="btn btn--ghost btn--sm" onClick={() => setBulk({ kind: 'idle' })}>
                        닫기
                      </button>
                    </div>
                  )}

                  {bulk.kind === 'confirm' && (
                    <ConfirmModal
                      title="gcode로 찾은 줄 일괄 저장"
                      message={
                        <>
                          <strong>{store.label}</strong>에서 옛 상품코드로 찾은{' '}
                          <strong>{num(gcodeTotal)}건</strong>의 BarCode1 에 제안된 바코드를 한 번에
                          써 넣습니다.
                          <br />
                          그 사이 다른 사람이 먼저 고친 줄은 건너뛰고, 나머지는 계속 진행합니다.
                        </>
                      }
                      confirmLabel="일괄 저장"
                      danger={false}
                      onConfirm={() => void runGcodeBulkApply()}
                      onClose={() => setBulk({ kind: 'idle' })}
                    />
                  )}

                  <p className="page-desc">읽은 시각 {list.data.at} (KST)</p>

                  {rows.length === 0 ? (
                    <EmptyBox>조건에 맞는 줄이 없습니다.</EmptyBox>
                  ) : (
                    <div className="table-wrap">
                      <table className="table inven-sync-table">
                        <thead>
                          <tr>
                            <th>POS BarCode</th>
                            <th>BarCode1 (직접 수정)</th>
                            <th>cate_cd</th>
                            <th style={{ textAlign: 'right' }}>상품코드</th>
                            <th style={{ textAlign: 'right' }}>옵션코드</th>
                            <th>상품명</th>
                            <th>연동상태</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row) => (
                            <Row
                              key={row.posBarcodeStored}
                              row={row}
                              labels={labels}
                              disabled={reload.isPending}
                              onSave={saveRow}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <Pagination
                    page={list.data.page}
                    totalPages={list.data.totalPages}
                    total={list.data.total}
                    onChange={(next) => update({ page: next })}
                  />
                </>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
}
